'use client';

/**
 * useServiceWorker Hook
 *
 * Handles service worker registration for:
 * - Web Push notifications
 * - Offline support (future)
 * - Background sync (future)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '@/lib/logger';

interface ServiceWorkerState {
  isSupported: boolean;
  isRegistered: boolean;
  registration: ServiceWorkerRegistration | null;
  error: string | null;
}

interface UseServiceWorkerOptions {
  /** Auto-register on mount */
  autoRegister?: boolean;
  /** Service worker path */
  swPath?: string;
  /** Update check interval in ms (0 = no auto-check) */
  updateCheckInterval?: number;
}

export function useServiceWorker(options: UseServiceWorkerOptions = {}) {
  const {
    autoRegister = true,
    swPath = '/sw.js',
    updateCheckInterval = 0,
  } = options;

  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: false,
    isRegistered: false,
    registration: null,
    error: null,
  });

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Check if service workers are supported
   */
  const checkSupport = useCallback((): boolean => {
    const supported =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window;

    setState((prev) => ({ ...prev, isSupported: supported }));
    return supported;
  }, []);

  /**
   * Register the service worker
   */
  const register = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      setState((prev) => ({
        ...prev,
        error: 'Service workers not supported',
      }));
      return null;
    }

    try {
      logger.debug('ServiceWorker', 'Registering service worker');

      const registration = await navigator.serviceWorker.register(swPath, {
        scope: '/',
      });
      if (!mountedRef.current) return registration;

      logger.info('ServiceWorker', 'Registered successfully', { scope: registration.scope });

      setState({
        isSupported: true,
        isRegistered: true,
        registration,
        error: null,
      });

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller &&
              mountedRef.current
            ) {
              logger.info('ServiceWorker', 'Update available');
              setUpdateAvailable(true);
            }
          });
        }
      });

      // Check if already installed and controlling
      if (registration.active) {
        logger.debug('ServiceWorker', 'Already active');
      }

      return registration;
    } catch (err) {
      logger.error('ServiceWorker', 'Registration failed', { error: err });
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : 'Registration failed',
        }));
      }
      return null;
    }
  }, [swPath]);

  /**
   * Unregister the service worker
   */
  const unregister = useCallback(async (): Promise<boolean> => {
    if (!state.registration) {
      return false;
    }

    try {
      const success = await state.registration.unregister();
      if (success) {
        logger.info('ServiceWorker', 'Unregistered successfully');
        setState({
          isSupported: true,
          isRegistered: false,
          registration: null,
          error: null,
        });
      }
      return success;
    } catch (err) {
      logger.error('ServiceWorker', 'Unregister failed', { error: err });
      return false;
    }
  }, [state.registration]);

  /**
   * Check for updates manually
   */
  const checkForUpdates = useCallback(async (): Promise<void> => {
    if (!state.registration) {
      return;
    }

    try {
      await state.registration.update();
      logger.debug('ServiceWorker', 'Update check completed');
    } catch (err) {
      logger.warn('ServiceWorker', 'Update check failed', { error: err });
    }
  }, [state.registration]);

  /**
   * Apply pending update (activates new service worker)
   */
  const applyUpdate = useCallback((): void => {
    if (!state.registration?.waiting) {
      return;
    }

    // Tell the waiting service worker to skip waiting
    state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Reload to use new service worker
    window.location.reload();
  }, [state.registration]);

  /**
   * Get current service worker state
   */
  const getState = useCallback(async (): Promise<string> => {
    if (!state.registration) {
      return 'not-registered';
    }

    if (state.registration.installing) {
      return 'installing';
    }
    if (state.registration.waiting) {
      return 'waiting';
    }
    if (state.registration.active) {
      return 'active';
    }

    return 'unknown';
  }, [state.registration]);

  // Auto-register on mount
  useEffect(() => {
    checkSupport();

    if (autoRegister && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      register();
    }
  }, [autoRegister, checkSupport, register]);

  // Set up periodic update checks
  useEffect(() => {
    if (!updateCheckInterval || !state.registration) {
      return;
    }

    const intervalId = setInterval(checkForUpdates, updateCheckInterval);

    return () => clearInterval(intervalId);
  }, [updateCheckInterval, state.registration, checkForUpdates]);

  // Listen for controller change (new service worker activated)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleControllerChange = () => {
      logger.info('ServiceWorker', 'Controller changed');
      setUpdateAvailable(false);
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  return {
    // State
    isSupported: state.isSupported,
    isRegistered: state.isRegistered,
    registration: state.registration,
    error: state.error,
    updateAvailable,

    // Actions
    register,
    unregister,
    checkForUpdates,
    applyUpdate,

    // Utils
    checkSupport,
    getState,
  };
}

export default useServiceWorker;
